"use client";

import { cn } from "@/lib/utils";
import { IAccountAssets, IBlocks, ITheme, IUserData } from "@/types";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import React from "react";
import FourthwallCustomization from "./tabs/fourthwall/customization";
import BlockToggle from "./tabs/toggle";
import FourthwallOptions from "./tabs/fourthwall/options";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    impersonate?: IUserData | null;
}
export default function BlockFourthWallProducts({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    impersonate,
}: Props) {
    let storeButtonStyle = {
        ...style,
        background: style.background,
        color: style.color,
    };
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [data, setData] = React.useState<any>({
        uniqueItems: [],
    });
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const ids = blockUpdate.options?.items
                    ?.map((item: any) => item.id)
                    .join(",");
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_API_SERVER}/fourthwall/${blockUpdate.url.replaceAll("https://", "")}/unique-items?items=${ids}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                if (!data.error) {
                    setData(data);
                } else {
                    setData({
                        uniqueItems: [],
                    });
                }
            } catch (error) {
                console.error(error);
                setData({
                    uniqueItems: [],
                });
            }
        };
        fetchData();
    }, [user, blockUpdate.options?.itemsm, blockUpdate.url]);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const items = data?.uniqueItems || [];
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Fourthwall Products",
            description:
                "Add products from your Fourthwall store to your website.",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <FourthwallOptions
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        impersonate={impersonate}
                    />
                    <FourthwallCustomization
                        block={blockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate, data?.uniqueItems]);
    const visibleItems = blockUpdate.options?.items
        ?.map((item: any) => {
            const matchingItem = items.find((i: any) => i.id === item.id);
            if (matchingItem) {
                // Return the matching item with its original 'sort' value
                return {
                    ...matchingItem,
                    sort: item.sort, // Ensure the 'sort' value remains intact
                };
            }
            return null; // In case no matching item is found
        })
        .filter(Boolean); // Filter out any null values in case of no matches
    const getGridCols = (itemCount: number) => {
        switch (itemCount) {
            case 1:
                return "grid-cols-3 [&>*:first-child]:col-span-3";
            case 2:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:last-child]:col-span-1";
            case 3:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*]:col-span-1 [&>*:last-child]:col-span-full";
            case 4:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:last-child]:col-span-2";
            case 5:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-1 [&>*:last-child]:col-span-1";
            case 6:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:last-child]:col-span-1";
            case 7:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2";
            case 8:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 9:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 10:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 ";
            default:
                return "grid-cols-3 [&>*:nth-child(3n+1)]:col-span-1 [&>*:nth-child(3n+2)]:col-span-1 [&>*:nth-child(3n)]:col-span-1";
        }
    };
    const productBackground = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_background
        : user?.theme.button;
    const productText = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_text
        : user?.theme.text_col_button;
    if (blockUpdate.options?.custom_theme) {
        storeButtonStyle.background =
            blockUpdate.options?.store_button_background;
        storeButtonStyle.color = blockUpdate.options?.store_button_text;
    }
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Fourthwall Products"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="FourthwallProducts"
            >
                <div
                    className={cn(
                        "grid w-full grid-cols-1 gap-1",
                        getGridCols(visibleItems.length)
                    )}
                >
                    {visibleItems.map((item: any) => (
                        <a
                            href={`${item.link}?utm_source=doras-profile&utm_medium=profile-click`}
                            target="_blank"
                            // rel=""
                            key={item.id}
                            className={cn(
                                "relative flex scale-95 flex-col items-center gap-0 transition-transform duration-300 hover:scale-100",
                                user?.theme.button_rounded === "rounded-full"
                                    ? "rounded-md"
                                    : user?.theme.button_rounded
                            )}
                        >
                            <img
                                src={item.image}
                                alt={item.title}
                                className={cn(
                                    "h-64 w-full overflow-hidden object-cover",
                                    user?.theme.button_rounded ===
                                        "rounded-full"
                                        ? "rounded-md"
                                        : user?.theme.button_rounded
                                )}
                            />
                            <div
                                className={cn(
                                    "absolute inset-x-0 bottom-0 flex w-full flex-col items-start gap-2 px-1",
                                    user?.theme.button_rounded ===
                                        "rounded-full"
                                        ? "rounded-md"
                                        : user?.theme.button_rounded,
                                    "rounded-t-none"
                                )}
                                style={{
                                    background: productBackground,
                                    color: productText,
                                }}
                            >
                                <h3 className="w-full truncate text-ellipsis text-lg font-semibold">
                                    {item.title}
                                </h3>
                                <p
                                    className="text-sm"
                                    style={{
                                        color: productText,
                                    }}
                                >
                                    {item.price}
                                </p>
                            </div>
                        </a>
                    ))}
                </div>
                <a className={`w-full`} href={blockUpdate.url} target="_blank">
                    <button
                        id="store-button"
                        className={`{formatClassName(text)} 
                
                ${user?.theme?.button_rounded}
                w-full 
                px-1 
                py-3 text-xl
                 transition-all 
                duration-150 hover:scale-105
                
                `}
                        style={storeButtonStyle}
                    >
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            See More
                        </span>
                    </button>
                </a>
            </div>
        </AdminPopover>
    );
}
